import { useContext, useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { getGatepassApi, deleteGatepassApi } from '../api/api.js';
import { AuthContext } from '../context/AuthContext.jsx';
import { useCompany } from '../context/CompanyContext.jsx';
import Card from '../components/ui/Card.jsx';
import Pagination from '../components/ui/Pagination.jsx';
import { usePagination } from '../hooks/usePagination.js';
import 'react-datepicker/dist/react-datepicker.css';

const SHED_OPTIONS = [
  { value: 'Shed1', label: 'Shed1' },
  { value: 'Shed2', label: 'Shed2' },
  { value: 'Shed3', label: 'Shed3' },
];

const PRODUCT_OPTIONS = [
  { value: 'Potato Sona Chips', label: 'Potato Sona Chips' },
  { value: 'Sugar Free Potato', label: 'Sugar Free Potato' },
  { value: 'New Patato', label: 'New Patato' },
];

export default function GatepassList() {
  const { user } = useContext(AuthContext);
  const { companyId } = useCompany();
  const navigate = useNavigate();
  const [limit] = useState(30);
  const [total, setTotal] = useState(0);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [gatepassNo, setGatepassNo] = useState('');
  const [kisanName, setKisanName] = useState('');
  const [selectedShed, setSelectedShed] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const pagination = usePagination(total, limit);

  useEffect(() => {
    const params = {
      page: pagination.currentPage,
      limit,
      startDate: startDate.toISOString().slice(0, 10),
      endDate: endDate.toISOString().slice(0, 10),
      ...(gatepassNo ? { gatepassNo } : {}),
      ...(kisanName ? { kisanName } : {}),
      ...(selectedShed?.value ? { shed: selectedShed.value } : {}),
      ...(selectedProduct?.value ? { productName: selectedProduct.value } : {}),
      ...(companyId ? { companyId } : {}),
    };
    setLoading(true);
    getGatepassApi(params)
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        setRows(Array.isArray(data) ? data : (data.rows ?? []));
        setTotal(res.data?.totalCount ?? 0);
      })
      .catch(() => {
        setRows([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [
    pagination.currentPage,
    limit,
    startDate,
    endDate,
    gatepassNo,
    kisanName,
    selectedShed?.value,
    selectedProduct?.value,
    companyId,
  ]);

  const handleDelete = (row) => {
    if (!window.confirm('Are you sure you want to delete this gatepass?')) return;
    setDeletingId(row.id);
    deleteGatepassApi(row.id)
      .then(() => {
        const params = {
          page: pagination.currentPage,
          limit,
          startDate: startDate.toISOString().slice(0, 10),
          endDate: endDate.toISOString().slice(0, 10),
          ...(gatepassNo ? { gatepassNo } : {}),
          ...(kisanName ? { kisanName } : {}),
          ...(selectedShed?.value ? { shed: selectedShed.value } : {}),
          ...(selectedProduct?.value ? { productName: selectedProduct.value } : {}),
          ...(companyId ? { companyId } : {}),
        };
        return getGatepassApi(params);
      })
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        setRows(Array.isArray(data) ? data : (data.rows ?? []));
        setTotal(res.data?.totalCount ?? 0);
      })
      .catch(() => {})
      .finally(() => setDeletingId(null));
  };

  const handleStartDateChange = (date) => {
    if (date) setStartDate(date);
    pagination.resetPagination?.();
  };
  const handleEndDateChange = (date) => {
    if (date) setEndDate(date);
    pagination.resetPagination?.();
  };

  return (
    <Card title="Gatepass List" className="gatepass-list-card">
      <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
        <div>
          <label className="form-label small mb-1">Start Date</label>
          <ReactDatePicker
            selected={startDate}
            onChange={handleStartDateChange}
            dateFormat="dd-MM-yyyy"
            className="form-control form-control-sm"
            maxDate={endDate}
          />
        </div>
        <div>
          <label className="form-label small mb-1">End Date</label>
          <ReactDatePicker
            selected={endDate}
            onChange={handleEndDateChange}
            dateFormat="dd-MM-yyyy"
            className="form-control form-control-sm"
            maxDate={new Date()}
            minDate={startDate}
          />
        </div>
        <div>
          <label className="form-label small mb-1">Gatepass No</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={gatepassNo}
            onChange={(e) => setGatepassNo(e.target.value)}
            placeholder="Gatepass no"
            style={{ minWidth: '120px' }}
          />
        </div>
        <div>
          <label className="form-label small mb-1">Kisan Name</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={kisanName}
            onChange={(e) => setKisanName(e.target.value)}
            placeholder="Kisan name"
            style={{ minWidth: '140px' }}
          />
        </div>
        <div>
          <label className="form-label small mb-1">Shed</label>
          <Select
            options={SHED_OPTIONS}
            value={selectedShed}
            onChange={setSelectedShed}
            placeholder="Shed"
            isClearable
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{ control: (base) => ({ ...base, minWidth: '100px' }) }}
          />
        </div>
        <div>
          <label className="form-label small mb-1">Product</label>
          <Select
            options={PRODUCT_OPTIONS}
            value={selectedProduct}
            onChange={setSelectedProduct}
            placeholder="Product"
            isClearable
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{ control: (base) => ({ ...base, minWidth: '160px' }) }}
          />
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => navigate('/gatepass')}
        >
          Add Gatepass
        </button>
      </div>

      {loading ? (
        <p className="text-muted">Loading...</p>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Gatepass No</th>
                  <th>Gatepass Date</th>
                  <th>Kisan Name</th>
                  <th>Shed</th>
                  <th>Product Name</th>
                  <th>Bag</th>
                  <th>Qtl</th>
                  <th>Vehicle No</th>
                  <th>Sync</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center text-muted py-4">
                      No records found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={r.id || idx}>
                      <td>{(pagination.currentPage - 1) * limit + idx + 1}</td>
                      <td>{r.gatepassNo ?? '—'}</td>
                      <td>{r.gatepassDate ?? '—'}</td>
                      <td>{r.kisanName ?? '—'}</td>
                      <td>{r.shed ?? '—'}</td>
                      <td>{r.productName ?? '—'}</td>
                      <td>{r.bag ?? '—'}</td>
                      <td>{r.qtl != null ? Number(r.qtl) : '—'}</td>
                      <td>{r.vehicleNo ?? '—'}</td>
                      <td>
                        {r.isSynced ? (
                          <span className="badge bg-success">Synced</span>
                        ) : (
                          <span className="badge bg-secondary">Pending</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate('/gatepass/edit/' + r.id)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            disabled={deletingId === r.id}
                            onClick={() => handleDelete(r)}
                          >
                            {deletingId === r.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {total > 0 && (
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={pagination.goToPage}
            />
          )}
        </>
      )}
    </Card>
  );
}
